import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showSnackbar } from '../../../api/snackbarSlice';
import { useDispatch } from 'react-redux';
import { AxiosError } from 'axios';
import { apiClient } from '../../../api/axios';

export const generateOTP = async ({ id, action }) => {
  const { data } = await apiClient.post(`/webhook-configs/${id}/generate-otp/${action}`);
  return data;
};

export const viewSecret = async ({ id, otp }) => {
  const { data } = await apiClient.post(`/webhook-configs/${id}/view-secret`, { otp });
  return data;
};

export const regenerateSecret = async ({ id, otp }) => {
  const { data } = await apiClient.post(`/webhook-configs/${id}/regenerate-secret`, { otp });
  return data;
};

export const useGenerateOTPHandler = ({ onSuccess }) => {
  const dispatch = useDispatch();

  const mutation = useMutation({
    mutationFn: generateOTP,
    onSuccess: (data, variables) => {
      dispatch(
        showSnackbar({
          message: data.message || 'OTP sent to your email',
          severity: 'success',
        }),
      );
      onSuccess && onSuccess(variables);
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        console.log(error.response);
        dispatch(
          showSnackbar({
            message: error.response?.data || 'Failed to send OTP',
            severity: 'error',
          }),
        );
      } else {
        dispatch(
          showSnackbar({
            message: 'Failed to send OTP. Please try again.',
            severity: 'error',
          }),
        );
      }
    },
  });

  const onSubmit = ({ id, action }) => {
    mutation.mutate({ id, action });
  };

  return {
    data: mutation.data,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    onSubmit,
  };
};

export const useViewSecretHandler = ({ onSuccess }) => {
  const dispatch = useDispatch();

  const mutation = useMutation({
    mutationFn: viewSecret,
    onSuccess: (data, variables) => {
      dispatch(
        showSnackbar({
          message: 'Secret revealed successfully',
          severity: 'success',
        }),
      );
      onSuccess && onSuccess(data, variables);
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        dispatch(
          showSnackbar({
            message: error.response?.data?.message || 'Invalid OTP',
            severity: 'error',
          }),
        );
      } else {
        dispatch(
          showSnackbar({
            message: 'Invalid OTP. Please try again.',
            severity: 'error',
          }),
        );
      }
    },
  });

  const onSubmit = ({ id, otp }) => {
    mutation.mutate({ id, otp });
  };

  return {
    data: mutation.data,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    onSubmit,
  };
};

export const useRegenerateSecretHandler = ({ onSuccess }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: regenerateSecret,

    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });

      dispatch(
        showSnackbar({
          message: 'Secret regenerated successfully',
          severity: 'success',
        }),
      );

      onSuccess && onSuccess(data, variables);
    },

    onError: (error) => {
      if (error instanceof AxiosError) {
        dispatch(
          showSnackbar({
            message: error.response?.data?.message || 'Invalid OTP',
            severity: 'error',
          }),
        );
      } else {
        dispatch(
          showSnackbar({
            message: 'Invalid OTP. Please try again.',
            severity: 'error',
          }),
        );
      }
    },
  });

  const onSubmit = ({ id, otp }) => {
    mutation.mutate({ id, otp });
  };

  return {
    data: mutation.data,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    onSubmit,
  };
};
